import React from 'react';
import { Space, Typography } from 'antd';
import { formatHumanDate, normalizeTitle } from '../../../utils/format';
import { LeadOpsMatch } from '../../../api/leadOps';

const { Text } = Typography;

const getColumnText = (columns: LeadOpsMatch['columns_preview'] = [], title: string) => {
  const normalizedTitle = normalizeTitle(title);
  const match = columns.find((col) => normalizeTitle(col?.title) === normalizedTitle);
  return match?.text || '';
};

const INVENTORY_STATUS_COLORS: Record<string, string> = {
  'ready to sell': 'green',
  'critical to sell': 'red',
  'to prolong': 'orange',
  'sold to advertiser': 'blue',
};

export const getInventoryStatus = (match: LeadOpsMatch): string =>
  getColumnText(match?.columns_preview, 'Inventory item status').trim();

export const inventoryStatusColor = (status: string): string =>
  INVENTORY_STATUS_COLORS[status.trim().toLowerCase()] || 'default';

export const MatchPreview: React.FC<{ match: LeadOpsMatch }> = ({ match }) => {
  const columns = match?.columns_preview || [];
  if (!columns.length) {
    return <Text type="secondary">No preview data.</Text>;
  }

  const isInventory = (match?.board_name || '').trim().toLowerCase() === 'inventory';
  const rows = (isInventory
    ? [
        { label: 'Inventory item status', value: getColumnText(columns, 'Inventory item status') },
        { label: "Publisher's deal type", value: getColumnText(columns, "Publisher's deal type") },
        { label: 'Publishers rate', value: getColumnText(columns, 'Publishers rate') },
        { label: 'Publisher Manager', value: getColumnText(columns, 'Publisher Manager') },
        { label: 'Ad Format', value: getColumnText(columns, 'Ad Format') },
        { label: 'Expected daily impressions/clicks', value: getColumnText(columns, 'Expected daily impressions/clicks') },
        { label: 'Zone ID', value: getColumnText(columns, 'Zone ID') },
        { label: 'Last updated', value: formatHumanDate(getColumnText(columns, 'Last updated')) },
        { label: 'Creation log', value: formatHumanDate(getColumnText(columns, 'Creation log')) },
      ]
    : [
        { label: 'Referred by', value: getColumnText(columns, 'Referred by') },
        { label: 'Manager', value: getColumnText(columns, 'Manager') },
        { label: 'Lead status', value: getColumnText(columns, 'Lead status') },
        { label: 'Partner Type', value: getColumnText(columns, 'Partner Type') },
        { label: 'Source of lead', value: getColumnText(columns, 'Source of lead') },
        { label: 'Source Name', value: getColumnText(columns, 'Source Name') },
        { label: 'Last updated', value: formatHumanDate(getColumnText(columns, 'Last updated')) },
        { label: 'Creation log', value: formatHumanDate(getColumnText(columns, 'Creation log')) },
      ]
  ).filter((row) => row.value);

  if (!rows.length) {
    return <Text type="secondary">No preview data.</Text>;
  }

  return (
    <Space className="leadops-match-preview" direction="vertical" size={6}>
      {rows.map((row) => (
        <div className="leadops-match-preview-row" key={row.label}>
          <Text className="leadops-match-preview-label" type="secondary">{row.label}:</Text>
          <Text className="leadops-match-preview-value">{row.value}</Text>
        </div>
      ))}
    </Space>
  );
};
